import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { IFormReportView } from '@fi-app/fi-form/fi-form-report/fi-form-report';
import { BehaviorSubject, Subject } from 'rxjs';
import { ObjectTypes } from '@fi-shared/types';
import { PageTitleService } from '@fi-app/utilities/fi-services/page-title.service';
import { FiFormHelperService } from '@fi-shared/fi-form-shared/fi-form-helper.service';
import { FiPrintDownloadComponent } from '@fi-shared/fi-print-download/fi-print-download.component';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { isDefined } from '@fi-shared/utils';
import { takeUntil } from 'rxjs/operators';
import { FiFormService } from '@fi-app/fi-form/fi-form.service';
import { FiFormReportComponent } from '@fi-app/fi-form/fi-form-report/fi-form-report.component';
import { EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { AppTextService } from '@fi-shared/app-text';

@Component({
    selector: 'fi-form-report-container',
    template: `
        <div class="fi-container">
          <fi-page-header [editTabsSettings]="EditTabs" [isMobileMenuShown]="false" [isSaveButtonShown]="false">
            <ng-container ngProjectAs="left-aligned-content">
              @if (isPrintSummaryButtonShown()) {
                <button
                  [matMenuTriggerFor]="downloadReportMenu"
                  mat-icon-button
                  [attr.title]="'common.download' | fiAppText"
                  >
                  <mat-icon class="fas fa-download"></mat-icon>
                </button>
              }
              <fi-print-download
                class="hide-mobile"
                [hidden]="!isPrintButtonShown"
                [printElementSelector]="'.fi-form-report-print-card'"
                [isPrintButtonShown]="false"
                [isNewLayout]="true"
                [downloadFilename]="formReport.Name"
                (onGeneratingPdfHtml)="onGeneratingPdfHtml($event)"
                >
              </fi-print-download>
            </ng-container>
          </fi-page-header>
        
          <div class="fi-content ExtraLightBackgroundColor">
            <fi-form-report
              [formReport]="formReport"
              [isCompleteReport]="isCompleteReport$ | async"
              (reportPosChange)="onReportPosChange($event)"
            ></fi-form-report>
          </div>
        </div>
        
        <mat-menu #downloadReportMenu="matMenu">
          <button mat-menu-item (click)="downloadReportPdf()">
            {{ 'form.form_report_download_summary' | fiAppText }}
          </button>
          <button mat-menu-item [matMenuTriggerFor]="downloadCompleteReportMenu">
            {{ 'form.form_report_download_full' | fiAppText }}
          </button>
        </mat-menu>
        
        <mat-menu #downloadCompleteReportMenu="matMenu">
          <button mat-menu-item (click)="downloadCompleteReportExcel()">Excel</button>
          <button mat-menu-item (click)="downloadReportPdf(true)">PDF</button>
        </mat-menu>
        `,
    styleUrls: ['./fi-form-report-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class FiFormReportContainerComponent implements OnInit, OnDestroy {
    @Input() formReport: IFormReportView;
    @ViewChild(FiPrintDownloadComponent) fiPrintDownloadComponent: FiPrintDownloadComponent;
    @ViewChild(FiFormReportComponent) fiFormReportComponent: FiFormReportComponent;
    buttonTitle$ = new BehaviorSubject('common.close');
    isPrintButtonShown: boolean = false;
    EditTabs: EditTabsModel;

    get isCompleteReport$() {
        return this.formHelperService.isCompleteReport$;
    }

    constructor(
        private pageTitleService: PageTitleService,
        private formHelperService: FiFormHelperService,
        private formService: FiFormService,
        private cdr: ChangeDetectorRef,
        private stateService: FiStateService,
        private appTextService: AppTextService,
    ) {}

    ngOnInit() {
        this.EditTabs = new EditTabsModel(
            this.appTextService.get('form.form_report'),
            this.appTextService.get(this.buttonTitle$.value),
        );
        this.pageTitleService.setTitleByName(this.formReport.Name, ObjectTypes.Form);
        this.formHelperService.isPrintButtonShown$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((value: boolean) => {
            this.isPrintButtonShown = value;
            this.cdr.detectChanges();
        });
    }

    isPrintSummaryButtonShown() {
        return !isDefined(this.stateService.params.pos);
    }

    onReportPosChange(newPos: string) {
        this.buttonTitle$.next(newPos ? 'back' : 'common.close');
        this.EditTabs.ButtonBackTitle = this.appTextService.get(this.buttonTitle$.value);
    }

    onGeneratingPdfHtml(isGeneratingPdfHtml: boolean) {
        this.formHelperService.onGeneratingPdfHtml(isGeneratingPdfHtml);
    }

    downloadReportPdf(isComplete?: boolean) {
        this.formHelperService.downloadPdf(this.fiPrintDownloadComponent, { isCompleteReport: isComplete });
    }

    downloadCompleteReportExcel() {
        this.formService.downloadReportExcel(
            this.formReport.Id,
            this.formReport.Name,
            this.fiFormReportComponent.reportStartDate.toISOString(),
            this.fiFormReportComponent.reportEndDate.toISOString(),
        );
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
